import {
  DefaultMonitorConfig,
  EVENT_TYPE,
  VideoStreamController,
  VideoStreamEvent,
  VideoStreamObserver,
  VideoStreamSubject,
} from '../HlsVideoStream';
import {TestInferenceResults} from '../TestInferenceResults';
import React, {useCallback, useEffect, useState} from 'react';
import {toast} from '../../../base-components/StudioToast';
import {TaskInfo} from '../../../types/metadata/TaskInfo';
import {useSelector} from 'react-redux';
import {RootState} from '../../../store';
import {DeploymentDetailsInfo} from '../../../types/deployment/DeploymentDetailsInfo';
import {Application} from '../../../types/project/Application';
import {Project} from '../../../types/project/Project';
import {InputSource} from '../../../types/deployment/InferenceInputSource';
import {setStreamReady, useDeploy} from '../useDeploy';

export type StreamListener = {
  isInProgress: () => boolean;
  setInProgress: (value: boolean) => void;
};

export type HlsContainerProps = {
  deployment: DeploymentDetailsInfo | null;
  inputSourceType: InputSource | '';
  streamSubject: VideoStreamSubject;
  deploymentApplication?: Application | null;
  isDeploying: boolean;
  projectData: Project;
  inProgressListener: StreamListener;
};

export const HlsContainer = ({
  deployment,
  inputSourceType,
  streamSubject,
  deploymentApplication,
  isDeploying,
  projectData,
  inProgressListener,
}: HlsContainerProps) => {
  const [videoController, setVideoController] = useState<VideoStreamController>();
  const serverConfig = useSelector((state: RootState) => state.config);
  const [, dispatch] = useDeploy();

  const startVideoStream = useCallback(
    (controller: VideoStreamController, taskInfo?: TaskInfo) => {
      if (
        taskInfo?.videoOutputHlsUrl &&
        taskInfo.gspBoxOutputUrl &&
        taskInfo.deploymentId
      ) {
        controller.start({
          limitedDuration: false,
          source: inputSourceType,
          url: taskInfo.videoOutputHlsUrl,
          sourceUrl: taskInfo.gspBoxOutputUrl,
          callerSession: taskInfo.callerSession,
          targetSession: taskInfo.targetSession,
          streamModel: 'PULL',
          projectId: projectData.id,
          serverConfig,
          monitorConfig: {
            ...DefaultMonitorConfig,
            monitorStream: true,
            projectId: projectData.id,
            deploymentId: taskInfo.deploymentId,
          },
          streamSubject,
        });
        inProgressListener.setInProgress(true);
      }
    },
    [inputSourceType, inProgressListener, projectData.id, serverConfig, streamSubject]
  );

  class GspTestStreamObserver extends VideoStreamObserver {
    controller: VideoStreamController;

    constructor(controller: VideoStreamController) {
      super();
      this.controller = controller;
    }

    handleEvent(event: VideoStreamEvent) {
      switch (event.type) {
        case EVENT_TYPE.READY_TO_START:
          startVideoStream(this.controller, event.taskInfo);
          break;
        case EVENT_TYPE.CONNECTION_FAILED:
          dispatch(setStreamReady(false));
          break;
        case EVENT_TYPE.ERROR:
          if (event.message) {
            toast.error(`Streaming error: ${event.message}`);
          } else {
            toast.error(`Streaming error. Please try again.`);
          }
          inProgressListener.setInProgress(false);
          dispatch(setStreamReady(false));
          break;
        case EVENT_TYPE.STREAM_FINISHED:
          inProgressListener.setInProgress(false);
          dispatch(setStreamReady(false));
          break;
        default:
          break;
      }
    }
  }

  useEffect(() => {
    const controller: VideoStreamController = new VideoStreamController();
    setVideoController(controller);
    streamSubject.addListener(
      'GspTestStreamObserver',
      new GspTestStreamObserver(controller)
    );

    const cleanup = () => {
      streamSubject.raiseStreamFinishEvent();
      dispatch(setStreamReady(false));
    };
    window.addEventListener('beforeunload', cleanup);

    return () => {
      window.removeEventListener('beforeunload', cleanup);
      cleanup();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputSourceType]);

  return (
    <TestInferenceResults
      deployment={deployment}
      hasTransformation={Boolean(deploymentApplication?.transformation)}
      isStreamingInference={inProgressListener.isInProgress()}
      isOtherTaskInProgress={isDeploying}
      projectData={projectData}
      videoController={videoController}
      streamSubject={streamSubject}
      displayRealtimeParameters={true}
    />
  );
};
